import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, Circle, ShoppingBag, ExternalLink } from "lucide-react";

// Phase 3G.2 — Retail Readiness tile.
// Compact onboarding checklist that ONLY renders when the tenant has
// `retail_mode_enabled = true`. For non-retail tenants this component
// returns null — guaranteeing zero visual impact on the existing B2B
// overview dashboard.
//
// Five rows, each computed from real data:
//   1. Retail mode enabled        ← /api/company
//   2. Catalog created            ← /api/retail/catalogs (count > 0)
//   3. Catalog published          ← any catalog has status: "published"
//   4. Assistant linked           ← /api/retail/assistant-links
//   5. Public storefront active   ← retail mode on AND a published catalog exists
//
// The tile is intentionally lightweight — no charts, no analytics, no
// modals. Each pending row has a single CTA link to the page that
// completes that step.

const ROW_TESTID = (id) => `retail-readiness-row-${id}`;

export default function RetailReadinessTile() {
  const [state, setState] = useState({
    loading: true,
    visible: false,
    slug: null,
    retailEnabled: false,
    hasCatalog: false,
    hasPublished: false,
    hasAssistantLink: false,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await api.get("/company");
        const company = c?.data?.company || {};
        const chatbot = c?.data?.chatbot || {};
        const retailEnabled = Boolean(company.retail_mode_enabled);
        if (!retailEnabled) {
          // Hide the tile entirely for B2B-only tenants.
          if (alive) setState((s) => ({ ...s, loading: false, visible: false, retailEnabled: false }));
          return;
        }
        // Retail is on — gather the rest in parallel. Both endpoints
        // require retail mode, so if either 403s we know the flag is
        // not actually live and we'll hide the tile.
        const [cats, links] = await Promise.all([
          api.get("/retail/catalogs").then((r) => r.data).catch(() => ({ catalogs: [] })),
          api.get("/retail/assistant-links").then((r) => r.data).catch(() => ({ links: [] })),
        ]);
        const catalogs = cats.catalogs || [];
        const links_ = links.links || [];
        if (!alive) return;
        setState({
          loading: false,
          visible: true,
          slug: chatbot.slug || null,
          retailEnabled: true,
          hasCatalog: catalogs.length > 0,
          hasPublished: catalogs.some((c) => c.status === "published"),
          hasAssistantLink: links_.length > 0,
        });
      } catch {
        // Safe default — hide on any unexpected error.
        if (alive) setState((s) => ({ ...s, loading: false, visible: false }));
      }
    })();
    return () => { alive = false; };
  }, []);

  if (state.loading || !state.visible) return null;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const storefrontUrl = state.slug ? `${origin}/retail/${state.slug}` : "";
  const storefrontActive = state.retailEnabled && state.hasPublished && Boolean(state.slug);

  const rows = [
    {
      id: "retail-mode",
      label: "Retail mode enabled",
      done: state.retailEnabled,
      // The tile is hidden when retail mode is off, so this row is always done.
      cta: null,
    },
    {
      id: "catalog-created",
      label: "Catalog created",
      done: state.hasCatalog,
      cta: state.hasCatalog
        ? { to: "/dashboard/retail-catalogs", label: "Manage" }
        : { to: "/dashboard/retail-catalogs", label: "Create catalog" },
    },
    {
      id: "catalog-published",
      label: "Catalog published",
      done: state.hasPublished,
      cta: state.hasPublished
        ? null
        : { to: "/dashboard/retail-catalogs", label: "Publish catalog" },
    },
    {
      id: "assistant-linked",
      label: "Assistant linked",
      done: state.hasAssistantLink,
      cta: state.hasAssistantLink
        ? null
        : { to: "/dashboard/retail-catalogs", label: "Link assistant" },
    },
    {
      id: "storefront-active",
      label: "Public storefront active",
      done: storefrontActive,
      cta: storefrontActive && storefrontUrl
        ? { href: storefrontUrl, external: true, label: "Open storefront" }
        : null,
    },
  ];

  const doneCount = rows.filter((r) => r.done).length;

  return (
    <section
      data-testid="retail-readiness-tile"
      className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center">
          <ShoppingBag className="w-4 h-4" />
        </div>
        <div>
          <div className="font-medium text-sm" data-testid="retail-readiness-title">Retail Readiness</div>
          <div className="text-xs text-muted-foreground">
            <span data-testid="retail-readiness-progress">{doneCount} of {rows.length}</span> steps complete
          </div>
        </div>
      </div>
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <li
            key={row.id}
            data-testid={ROW_TESTID(row.id)}
            className="flex items-center justify-between gap-3 py-1.5"
          >
            <span className="flex items-center gap-2 text-sm">
              {row.done ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid={`retail-readiness-icon-${row.id}-done`} />
              ) : (
                <Circle className="w-4 h-4 text-slate-300" data-testid={`retail-readiness-icon-${row.id}-pending`} />
              )}
              <span className={row.done ? "text-slate-700" : "text-slate-900"}>{row.label}</span>
            </span>
            {!row.done && row.cta && (
              row.cta.external ? (
                <a
                  href={row.cta.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`retail-readiness-cta-${row.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
                >
                  {row.cta.label} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <Link
                  to={row.cta.to}
                  data-testid={`retail-readiness-cta-${row.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
                >
                  {row.cta.label}
                </Link>
              )
            )}
            {row.done && row.cta && (
              row.cta.external ? (
                <a
                  href={row.cta.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`retail-readiness-cta-${row.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md text-slate-500 hover:text-slate-900"
                >
                  {row.cta.label} <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <Link
                  to={row.cta.to}
                  data-testid={`retail-readiness-cta-${row.id}`}
                  className="text-xs text-slate-500 hover:text-slate-900"
                >
                  {row.cta.label}
                </Link>
              )
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
